import { c as _c } from "react/compiler-runtime";
import { IcoCheckCircle16, IcoInfo16, IcoWarning16 } from '@onefootprint/icons';
import type { UserInsight } from '@onefootprint/request-types/dashboard';
import { Tooltip } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';

// Names arrive in title case ("Device ID"); sentence case matches the rest of the page, keeping acronyms.
const toSentenceCase = (name: string) => name.split(' ').map((word, index) => {
  if (/^[A-Z0-9]{2,}$/.test(word)) {
    return word;
  }
  const lower = word.toLowerCase();
  return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
}).join(' ');
type ItemProps = Pick<UserInsight, 'name' | 'value' | 'description' | 'insightUnit'>;
const Item = t0 => {
  const $ = _c(26);
  const {
    name,
    value,
    description,
    insightUnit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "otherInsights"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== t) {
    t2 = ms => {
      const days = Math.floor(ms / 86400000);
      const hours = Math.floor(ms / 3600000) % 24;
      const minutes = Math.floor(ms / 60000) % 60;
      const seconds = Math.floor(ms / 1000) % 60;
      return [days > 0 && t(_temp, {
        count: days
      }), hours > 0 && t(_temp2, {
        count: hours
      }), minutes > 0 && t(_temp3, {
        count: minutes
      }), (seconds > 0 || hours === 0 && minutes === 0) && t(_temp4, {
        count: seconds
      })].filter(Boolean).join(" ");
    };
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formatDuration = t2;
  let t3;
  if ($[3] !== formatDuration || $[4] !== insightUnit.data || $[5] !== insightUnit.kind || $[6] !== t || $[7] !== value) {
    t3 = () => {
      if (insightUnit.kind === "duration_ms") {
        return formatDuration(Number.parseInt(value, 10));
      }
      if (insightUnit.kind === "boolean") {
        const isTrue = value === "true";
        const isExpected = isTrue === (insightUnit.data.severity === "good");
        return <span className="flex items-center gap-1">{isTrue ? t(_temp5) : t(_temp6)}{isExpected ? <IcoCheckCircle16 color="success" /> : <IcoWarning16 color="error" />}<span className="sr-only">{isExpected ? t(_temp7) : t(_temp8)}</span></span>;
      }
      return value;
    };
    $[3] = formatDuration;
    $[4] = insightUnit.data;
    $[5] = insightUnit.kind;
    $[6] = t;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderValue = t3;
  let t4;
  if ($[9] !== name) {
    t4 = toSentenceCase(name);
    $[9] = name;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== description) {
    t5 = description && <div className="inline pointer-coarse:hidden"><Tooltip content={description}><button type="button" aria-label={description} className="ml-1.5 inline-flex cursor-default rounded-sm align-[-3px]"><IcoInfo16 color="tertiary" /></button></Tooltip></div>;
    $[11] = description;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="text-body-2 text-tertiary">{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== description) {
    t7 = description && <div className="col-span-2 row-start-2 hidden text-body-3 text-tertiary pointer-coarse:block">{description}</div>;
    $[16] = description;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== renderValue) {
    t8 = renderValue();
    $[18] = renderValue;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <p className="justify-self-end text-right text-body-2 text-primary tabular-nums wrap-anywhere">{t8}</p>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t9) {
    t10 = <li className="grid grid-cols-[fit-content(60%)_minmax(0,1fr)] items-baseline gap-x-6 gap-y-0.5 px-4 py-1.5">{t6}{t7}{t9}</li>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
export default Item;
function _temp($) {
  return $.duration.days;
}
function _temp2($_0) {
  return $_0.duration.hours;
}
function _temp3($_1) {
  return $_1.duration.minutes;
}
function _temp4($_2) {
  return $_2.duration.seconds;
}
function _temp5($_3) {
  return $_3.yes;
}
function _temp6($_4) {
  return $_4.no;
}
function _temp7($_5) {
  return $_5.expected;
}
function _temp8($_6) {
  return $_6.unexpected;
}